import {
  ModuleStatus,
  ModuleStatusReason,
  ModuleType,
  ProxyAuthType,
  type Module,
  type ModuleStatusDto,
  type SetMaintenanceRequestBody,
  type UpdateModuleRequestBody,
} from "@eisox/api/core-api";
import dayjs, { type ManipulateType } from "@eisox/dayjs";
import type { AlertProps } from "@eisox/design-system";
import { z } from "@eisox/zod";

import { m } from "~/paraglide/messages";

import { isNiagara } from "./add-module.helper";

/**
 * Form model for the module detail page — field names mirror the `ModuleEntity`/`Proxy` shape.
 * `userName`/`password` are the PLC credentials; the nested `proxy` holds the GTB state (`isEnabled`,
 * display `name`) and the reverse-proxy operator credentials. All credentials are write-only (never
 * hydrated from the API).
 */
export interface ModuleSettingsForm {
  gatewayId: string;
  /** Address is `url` (IP) xor `bacnetDeviceId` — which one is editable is fixed by the module's mode. */
  url?: string;
  bacnetDeviceId?: number;
  userName: string;
  password: string;
  proxy: {
    /** GTB toggle — whether the module has a reverse proxy. */
    isEnabled: boolean;
    /** Niagara only — open GTB in a new tab instead of embedding an iframe. */
    isExternal: boolean;
    name: string;
    userName: string;
    password: string;
  };
  plcMaintenanceEnabled: boolean;
  plcMaintenanceDuration: string;
}

export const isPLCMaintenanceEnabled = (module: Module): boolean =>
  !!module.sshOperatorExpireAt && dayjs(module.sshOperatorExpireAt).isAfter(dayjs());

/** A module is BACnet-addressed (vs IP/`url`) when it carries a `bacnetDeviceId`. */
export const isBacnetModule = (module: Module): boolean => module.bacnetDeviceId != null;

export const moduleToFormValues = (module: Module): ModuleSettingsForm => ({
  gatewayId: module.gatewayId,
  url: module.url ?? "",
  bacnetDeviceId: module.bacnetDeviceId,
  userName: "",
  password: "",
  proxy: {
    isEnabled: !!module.proxy?.isEnabled,
    isExternal: module.proxy?.isExternal ?? false,
    name: module.proxy?.name ?? "",
    userName: "",
    password: "",
  },
  plcMaintenanceEnabled: isPLCMaintenanceEnabled(module),
  plcMaintenanceDuration: "",
});

export const moduleSettingsSchema = (module: Module) =>
  z
    .object({
      gatewayId: z.string().nonempty(),
      url: z.string().optional(),
      bacnetDeviceId: z.number().optional(),
      userName: z.string(),
      password: z.string(),
      proxy: z.object({
        isEnabled: z.boolean(),
        isExternal: z.boolean(),
        name: z.string(),
        userName: z.string(),
        password: z.string(),
      }),
      plcMaintenanceEnabled: z.boolean(),
      plcMaintenanceDuration: z.string(),
    })
    .superRefine((data, ctx) => {
      // Require whichever address the module actually uses (IP xor BACnet device ID).
      if (isBacnetModule(module)) {
        if (data.bacnetDeviceId == null)
          ctx.addIssue({ path: ["bacnetDeviceId"], code: "custom", message: m.module_settings_required() });
      } else if (!data.url) {
        ctx.addIssue({ path: ["url"], code: "custom", message: m.module_settings_required() });
      }
      // Only require operator creds when enabling a never-configured proxy; a configured-but-disabled
      // one keeps its stored credentials, so re-enabling needs no re-entry.
      if (data.proxy.isEnabled && !module.proxy) {
        if (!data.proxy.userName)
          ctx.addIssue({ path: ["proxy", "userName"], code: "custom", message: m.module_settings_required() });
        if (!data.proxy.password)
          ctx.addIssue({ path: ["proxy", "password"], code: "custom", message: m.module_settings_required() });
      }
      if (data.plcMaintenanceEnabled && !isPLCMaintenanceEnabled(module) && !data.plcMaintenanceDuration) {
        ctx.addIssue({ path: ["plcMaintenanceDuration"], code: "custom", message: m.module_settings_required() });
      }
    });

/**
 * Idempotent update body — the endpoint applies present fields and leaves absent ones untouched.
 * Write-only secrets are sent only when the user actually typed them. `proxy.enabled` toggles GTB while
 * keeping the stored credentials (they're re-sent only if re-entered).
 */
export const formToUpdateBody = (data: ModuleSettingsForm, module: Module): UpdateModuleRequestBody => ({
  gatewayId: data.gatewayId,
  // Address by whichever mode the module uses — never send both (backend rejects url + bacnetDeviceId).
  ...(isBacnetModule(module) ? { bacnetDeviceId: data.bacnetDeviceId } : { url: data.url }),
  userName: data.userName || undefined,
  password: data.password || undefined,
  proxy: {
    enabled: data.proxy.isEnabled,
    name: data.proxy.isEnabled ? data.proxy.name : undefined,
    ...(isNiagara(module.type) ? { isExternal: data.proxy.isExternal } : {}),
    ...(data.proxy.userName && data.proxy.password
      ? {
          userName: data.proxy.userName,
          password: data.proxy.password,
          type: isNiagara(module.type) ? ProxyAuthType.BASIC_AUTH : ProxyAuthType.COOKIE,
        }
      : {}),
  },
});

/**
 * PLC-maintenance changes go through the dedicated `.../maintenance` endpoint. Baseline is the module's
 * current state: (re)enable when a duration is chosen, disable when toggled off from an enabled state,
 * otherwise no-op.
 */
export const formToMaintenanceAction = (
  data: ModuleSettingsForm,
  module: Module,
): SetMaintenanceRequestBody | undefined => {
  if (data.plcMaintenanceEnabled && data.plcMaintenanceDuration) {
    const [amount, unit] = data.plcMaintenanceDuration.split(" ");
    if (!amount || !unit) return undefined;
    return {
      enabled: true,
      expireAt: dayjs()
        .add(parseInt(amount), unit as ManipulateType)
        .toISOString(),
    };
  }
  if (!data.plcMaintenanceEnabled && isPLCMaintenanceEnabled(module)) return { enabled: false };
  return undefined;
};

export const PLC_MAINTENANCE_DURATIONS: { value: string; name: string }[] = [
  { value: "30 minutes", name: m.module_duration_30_minutes() },
  { value: "1 hour", name: m.module_duration_1_hour() },
  { value: "6 hours", name: m.module_duration_6_hours() },
  { value: "24 hours", name: m.module_duration_24_hours() },
  { value: "48 hours", name: m.module_duration_48_hours() },
  { value: "7 days", name: m.module_duration_7_days() },
  { value: "1 month", name: m.module_duration_1_month() },
];

export const getModuleTypeLabel = (type: ModuleType): string => {
  switch (type) {
    case ModuleType.ECLYPSE:
      return m.module_type_eclypse();
    case ModuleType.ECLYPSE_RELAIS:
      return m.module_type_eclypseRelais();
    case ModuleType.ECLYPSE_BOILER_ROOM:
      return m.module_type_eclypseBoilerRoom();
    case ModuleType.REDIRECTION:
      return m.module_type_redirection();
    case ModuleType.NIAGARA_BOILER_ROOM:
      return m.module_type_niagaraBoilerRoom();
  }
};

/** Boiler-room module types (eclypse/niagara) — the ones listed in the modules settings sidebar. */
export const BOILER_ROOM_MODULE_TYPES = [ModuleType.ECLYPSE_BOILER_ROOM, ModuleType.NIAGARA_BOILER_ROOM];

export const MODULE_STATUS_POLL_INTERVAL_MS = 10_000;

export const getModuleStatusSeverity = (status?: ModuleStatusDto): AlertProps["severity"] =>
  status ? status.status : "help";

export const getModuleStatusLabel = (status?: ModuleStatusDto): string | undefined => {
  if (!status) return undefined;

  switch (status.reason) {
    case ModuleStatusReason.BOX_DISCONNECTED:
      return m.module_settings_status_box_disconnected();
    case ModuleStatusReason.GET_DATA_ERROR:
      return m.module_settings_status_plc_disconnected();
    case ModuleStatusReason.INSTALLING_WITH_GTB_PAGE:
      return m.module_settings_status_installing_with_gtb_page();
    case ModuleStatusReason.INSTALLING_WITHOUT_GTB_PAGE:
      return m.module_settings_status_installing_without_gtb_page();
  }

  switch (status.status) {
    case ModuleStatus.ERROR:
      return m.module_settings_status_plc_disconnected();
    case ModuleStatus.WARNING:
      return m.module_settings_status_installing();
    case ModuleStatus.SUCCESS:
      return m.module_settings_status_installed();
  }
};
